import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/config';
import OpsLayout from '../components/OpsLayout';
import opsStatusTone from '../utils/opsStatusTone';
import '../css/AdminApprovals.css';

// 관리자 가입 승인. 설계: docs/superpowers/specs/2026-09-16-admin-signup-approval.md
//
// 가입하면 계정은 만들어지되 어떤 관리그룹에도 들어가지 않는다. 최고관리자가 여기서
// 승인하면 그때 병원의 관리자 그룹·사용자 그룹이 (없으면) 만들어지고 구성원이 된다.
// Slack 버튼으로도 같은 처리를 할 수 있어서, 이 화면은 처리 결과가 이미 반영돼 있을 수
// 있다 — 서버가 already 를 주면 안내만 하고 목록을 다시 읽는다.

const TABS = [
    { key: 'PENDING', label: '대기' },
    { key: 'APPROVED', label: '승인' },
    { key: 'REJECTED', label: '거부' }
];

const STATUS_LABEL = { PENDING: '대기', APPROVED: '승인', REJECTED: '거부' };
// 대기는 '요청중'과 같은 노랑, 승인은 '수락됨'과 같은 초록, 거부는 회색이다.
const STATUS_TONE_SOURCE = { PENDING: '요청중', APPROVED: '수락됨', REJECTED: '거부' };

const formatDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export default function AdminApprovals() {
    const [status, setStatus] = useState('PENDING');
    const [requests, setRequests] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState('');
    const [busyId, setBusyId] = useState(null);
    const [rejecting, setRejecting] = useState(null);
    const [reason, setReason] = useState('');
    const [reasonError, setReasonError] = useState('');

    const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

    const fetchRequests = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/admin-signup-requests?status=${status}`,
                authHeaders()
            );
            setRequests(response.data?.requests || []);
        } catch (err) {
            setError(err.response?.data?.message || '신청 목록을 불러오지 못했습니다.');
        } finally {
            setIsLoading(false);
        }
    }, [status]);

    useEffect(() => { fetchRequests(); }, [fetchRequests]);

    const afterDecision = (result, approved) => {
        if (result === 'already') {
            setNotice('이미 처리된 신청입니다. 목록을 새로 읽었습니다.');
        } else if (result === 'not_found') {
            setNotice('신청을 찾을 수 없습니다. 목록을 새로 읽었습니다.');
        } else {
            setNotice(approved ? '승인했습니다.' : '거부했습니다.');
        }
        fetchRequests();
    };

    const approve = async (request) => {
        setBusyId(request.id);
        setNotice('');
        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/admin-signup-requests/${request.id}/approve`, {}, authHeaders()
            );
            afterDecision(response.data?.result, true);
        } catch (err) {
            setError(err.response?.data?.message || '승인 처리에 실패했습니다.');
        } finally {
            setBusyId(null);
        }
    };

    const submitReject = async () => {
        const trimmed = reason.trim();
        // 사유 없는 거부는 나중에 아무도 설명하지 못한다. 서버도 같은 규칙으로 막는다.
        if (!trimmed) {
            setReasonError('거부 사유를 입력해주세요.');
            return;
        }
        setBusyId(rejecting.id);
        setNotice('');
        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/admin-signup-requests/${rejecting.id}/reject`,
                { reason: trimmed },
                authHeaders()
            );
            setRejecting(null);
            setReason('');
            setReasonError('');
            afterDecision(response.data?.result, false);
        } catch (err) {
            setError(err.response?.data?.message || '거부 처리에 실패했습니다.');
        } finally {
            setBusyId(null);
        }
    };

    return (
        <OpsLayout
            title="관리자 가입 승인"
            description="가입 신청을 확인하고 승인하면 병원 그룹에 넣어요."
        >
            <section className="ops-page__card approvals-page">
                <div className="sds-toolbar">
                    <div className="sds-seg" role="tablist" aria-label="신청 상태">
                        {TABS.map((tab) => (
                            <button
                                key={tab.key}
                                role="tab"
                                aria-selected={status === tab.key}
                                className={`sds-seg__item${status === tab.key ? ' is-active' : ''}`}
                                onClick={() => { setStatus(tab.key); setNotice(''); }}
                            >
                                {tab.label}
                                {status === tab.key && requests.length > 0 ? ` ${requests.length}` : ''}
                            </button>
                        ))}
                    </div>
                </div>

                {notice && <div className="sds-notice">{notice}</div>}
                {error && <div className="sds-notice sds-notice--error">{error}</div>}

                {isLoading ? (
                    <div className="sds-notice">데이터를 불러오는 중입니다...</div>
                ) : (
                    <div className="sds-table-wrap">
                        <table className="sds-table">
                            <thead>
                                <tr>
                                    <th>신청일</th>
                                    <th>이름</th>
                                    <th>이메일</th>
                                    <th>연락처</th>
                                    <th>신청 병원</th>
                                    <th>상태</th>
                                    <th>처리</th>
                                </tr>
                            </thead>
                            <tbody>
                                {requests.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="sds-empty">
                                            {status === 'PENDING' ? '대기 중인 신청이 없습니다.' : '해당하는 신청이 없습니다.'}
                                        </td>
                                    </tr>
                                ) : requests.map((request) => (
                                    <tr key={request.id}>
                                        <td>{formatDate(request.created_at)}</td>
                                        <td>{request.name || '—'}</td>
                                        <td>{request.email || '—'}</td>
                                        <td>{request.mobile || '—'}</td>
                                        <td>
                                            {request.hospital_name || '—'}
                                            {request.district_name && <small className="approvals-page__sub">{request.district_name}</small>}
                                        </td>
                                        <td>
                                            <span className={`sds-badge sds-badge--${opsStatusTone(STATUS_TONE_SOURCE[request.status])}`}>
                                                {STATUS_LABEL[request.status] || request.status}
                                            </span>
                                        </td>
                                        <td>
                                            {request.status === 'PENDING' ? (
                                                <div className="approvals-page__actions">
                                                    <button
                                                        className="sds-btn sds-btn--primary sds-btn--sm"
                                                        disabled={busyId === request.id}
                                                        onClick={() => approve(request)}
                                                    >
                                                        승인
                                                    </button>
                                                    <button
                                                        className="sds-btn sds-btn--secondary sds-btn--sm"
                                                        disabled={busyId === request.id}
                                                        onClick={() => { setRejecting(request); setReason(''); setReasonError(''); }}
                                                    >
                                                        거부
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="approvals-page__decided">
                                                    <span>{formatDate(request.decided_at)}</span>
                                                    {request.decided_by_label && <small className="approvals-page__sub">{request.decided_by_label}</small>}
                                                    {request.reject_reason && <small className="approvals-page__sub">{request.reject_reason}</small>}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {rejecting && (
                <div className="sds-modal-overlay" onClick={() => setRejecting(null)}>
                    <div
                        className="sds-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="reject-modal-title"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="sds-modal__head">
                            <h2 id="reject-modal-title" className="sds-modal__title">가입 거부</h2>
                        </div>
                        <div className="sds-modal__body">
                            <p className="approvals-page__target">
                                {rejecting.name || '이름 없음'} · {rejecting.hospital_name || '병원 미상'}
                            </p>
                            <div className="sds-field">
                                <label className="sds-label" htmlFor="reject-reason">거부 사유</label>
                                <textarea
                                    id="reject-reason"
                                    className="sds-input approvals-page__reason"
                                    value={reason}
                                    onChange={(event) => { setReason(event.target.value); setReasonError(''); }}
                                    placeholder="예: 기관 확인이 되지 않습니다."
                                />
                                {reasonError && <p className="sds-field-error">{reasonError}</p>}
                            </div>
                        </div>
                        <div className="sds-modal__foot">
                            <button className="sds-btn sds-btn--secondary" onClick={() => setRejecting(null)}>취소</button>
                            <button
                                className="sds-btn sds-btn--primary"
                                disabled={busyId === rejecting.id}
                                onClick={submitReject}
                            >
                                거부
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </OpsLayout>
    );
}
